
import { computed, defineComponent, onBeforeMount, reactive } from 'vue'
import moment from 'moment'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheTable from '@/common/components/UI/TheTable/index.vue'
import TableScrollWrapper from '@/common/components/UI/TableScrollWrapper/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import Cheap from '@/common/components/UI/Cheap/index.vue'
import TheModal from '@/common/components/UI/Modal/index.vue'

// module components
import CitiesTableActions from '@/modules/CitiesModule/components/CitiesTableActions/index.vue'

// types
import { CityT, RegionT } from '@/core/types/cities.types'

// realisations
import { cities } from '@/core/realisations/cities.realisation'

// classes
import { DataState } from '@/core/classes/DataState.class'

// constants
import { CitiesColumns } from '@/core/constants/columns/Cities.columns'
import useNotifications from '@/common/composable/useNotifications'

export const STATUS_OPTIONS = [
    { id: 1, name: 'Активный' },
    { id: 2, name: 'Неактивный' },
]

export default defineComponent({
    components: {
        PageTitle,
        TheButton,
        FailedFetch,
        TheInput,
        TheSelect,
        TheTable,
        TableScrollWrapper,
        Cheap,
        CitiesTableActions,
        TheModal,
    },
    setup() {
        const notifications = useNotifications()

        const state = reactive(new DataState<CityT>({}))
        const cities_copy = reactive({ list: [] as CityT[], is_search: false })

        const filters_state = reactive({
            search_query: null as string | null,
            regionId: null as number | null,
            statusId: null as number | null,
            postCode: null as string | null,
            //
            limit: 50,
        })

        const delete_modal_state = reactive({
            is_opened: false,
            is_loading: false,
            city: null as CityT | null,
        })

        const loadCities = async (page: number) => {
            state.pagination.current_page = page

            try {
                state.is_loading = true

                const data = await cities.getAllList(
                    page,
                    filters_state.search_query as string,
                    filters_state.limit,
                    filters_state.regionId as number,
                    filters_state.statusId as number,
                    filters_state.postCode as string
                )
                state.list = data.data
                cities_copy.list = data.data

                state.pagination.total_pages = data.meta.last_page
                state.pagination.total_items = data.meta.total

                state.is_failed = false
                state.failure.error_state.is_cancel = false
            } catch (error) {
                state.failure.catchError(error)

                if (!state.failure.error_state.is_cancel) {
                    state.is_failed = true
                }
            } finally {
                if (!state.failure.error_state.is_cancel) {
                    state.is_loading = false
                }
            }
        }

        const onLimitChange = (limit: number) => {
            filters_state.limit = limit
            loadCities(state.pagination.current_page)
        }

        const data_state = reactive({
            regions: {
                is_loading: false,
                is_failed: false,
                list: [] as RegionT[],
            },
        })

        const loadRegions = async () => {
            try {
                data_state.regions.is_loading = true

                const data = await cities.getRegions()
                data_state.regions.list = data.data

                data_state.regions.is_failed = false
            } catch (error) {
                console.error(error)
                data_state.regions.is_failed = true
                notifications.notifyError({
                    error,
                    title: 'При получении регионов произошла ошибка',
                })
            } finally {
                data_state.regions.is_loading = false
            }
        }

        onBeforeMount(() => {
            loadCities(1)
            loadRegions()
        })

        const searchCity = async () => {
            cities_copy.is_search = true

            await loadCities(1)

            cities_copy.is_search = false
        }

        const resetFilter = async () => {
            filters_state.search_query = null
            filters_state.regionId = null
            filters_state.statusId = null
            filters_state.postCode = null
            await loadCities(1)
        }

        const onDeleteCity = (city: CityT) => {
            delete_modal_state.city = { ...city }
            delete_modal_state.is_opened = true
        }

        const onDeleteCityCancel = () => {
            delete_modal_state.is_opened = false
            delete_modal_state.city = null
        }

        const onDeleteCitySubmit = async () => {
            if (!delete_modal_state.city || delete_modal_state.is_loading) return

            try {
                delete_modal_state.is_loading = true

                await cities.deleteCity(delete_modal_state.city.id)
                notifications.notify({
                    type: 'success',
                    title: `Город ${delete_modal_state.city?.name} удален`,
                })

                delete_modal_state.is_opened = false
                delete_modal_state.city = null

                loadCities(state.pagination.current_page)
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: `При удалении города ${delete_modal_state.city?.name} произошла ошибка`,
                })
            } finally {
                delete_modal_state.is_loading = false
            }
        }

        const deleteCityText = computed(() => {
            if (delete_modal_state.city) {
                return `Вы действительно хотите удалить город ${delete_modal_state.city.name}?`
            }

            return ''
        })

        const formatDate = (dateStr: string) => {
            if (!dateStr) return '-'

            const dateFirst = dateStr.split(' ')[0]
            return dateFirst.split('-').reverse().join('.')
        }

        return {
            state,
            data_state,
            filters_state,
            delete_modal_state,
            cities_copy,
            loadCities,
            searchCity,
            resetFilter,
            onLimitChange,
            moment,
            //
            CitiesColumns,
            STATUS_OPTIONS,
            onDeleteCity,
            onDeleteCityCancel,
            onDeleteCitySubmit,
            formatDate,
            deleteCityText,
        }
    },
})
